// ✅ src/pages/Sales/FullPaymentReportAllPrint.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const FullPaymentReportAllPrint = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // read querystring from URL
  const params = useMemo(() => {
    const qs = new URLSearchParams(window.location.search);
    return {
      enquiryNumber: qs.get("enquiryNumber") || "",
      customerName: qs.get("customerName") || "",
      customerId: qs.get("customerId") || "",
      assetSubTypeId: qs.get("assetSubTypeId") || "",
      dob: qs.get("dob") || "",
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await axios.get(`${BASE_URL}/api/fullPaymentListing/search`, { params });

        if (!res.data?.success) {
          throw new Error(res.data?.message || "Failed to load report");
        }

        // backend already returns slNo etc
        setReportData(res.data.data || []);

        // ✅ optional: auto print once loaded
        setTimeout(() => window.print(), 300);
      } catch (e) {
        console.error(e);
        setError(e.message || "Error loading report");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [params]);

  const totalAmount = useMemo(
    () => reportData.reduce((sum, r) => sum + Number(r.amount || 0), 0),
    [reportData]
  );

  const fmt = (n) =>
    Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="max-w-4xl mx-auto p-4 bg-white font-sans text-sm print:p-0 print:max-w-none">
      {/* Header */}
      <div className="border-2 border-black mb-0">
        <div className="border-b border-black flex">
          <div className="w-32 border-r border-black p-2 flex items-center justify-center">
            <div className="bg-black text-white px-2 py-1 text-xs font-bold text-center">
              <div>NISSI</div>
              <div>INFOTECH PVT LTD</div>
            </div>
          </div>

          <div className="flex-1 p-3">
            <div className="text-center">
              <h1 className="text-lg font-bold mb-1">Nissi Infotech</h1>
              <div className="text-xs">
                <div>Nissi</div>
                <div className="font-bold">CIVIL CONSTRUCTION</div>
                <div>3/42-12, Nagappa colony, kavundampalayam,641600, Tamil Nadu, India.</div>
                <div>Phone No: 9003744644 infraconsoft-implementation@nissiinfotech.com</div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center py-2 border-b border-black font-bold">Full Payment Report</div>
      </div>

      {loading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          <div className="mt-2">Loading report data...</div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <div className="font-bold">Error loading report:</div>
          <div>{error}</div>
        </div>
      )}

      <div className="border-l-2 border-r-2 border-b-2 border-black">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-black bg-gray-100">
              <th className="border-r border-black px-2 py-2 w-12 text-center">S.No</th>
              <th className="border-r border-black px-2 py-2 w-20 text-center">Customer ID</th>
              <th className="border-r border-black px-2 py-2 w-32 text-center">Customer Name</th>
              <th className="border-r border-black px-2 py-2 w-20 text-center">Bill No</th>
              <th className="border-r border-black px-2 py-2 w-20 text-center">Plot Number</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Asset Code</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Asset Type/Model</th>
              <th className="border-r border-black px-2 py-2 w-24 text-center">Amount</th>
              <th className="px-2 py-2 w-20 text-center">Payment Status</th>
            </tr>
          </thead>

          <tbody>
            {reportData.map((r, index) => (
              <tr key={index} className="border-b border-gray-300">
                <td className="border-r border-black px-2 py-2 text-center">{r.slNo ?? index + 1}</td>
                <td className="border-r border-black px-2 py-2 text-center">{r.customerId}</td>
                <td className="border-r border-black px-2 py-2">{r.customerName}</td>
                <td className="border-r border-black px-2 py-2 text-center">{r.billNo}</td>
                <td className="border-r border-black px-2 py-2 text-center">{r.plotNumber}</td>
                <td className="border-r border-black px-2 py-2">{r.assetCode}</td>
                <td className="border-r border-black px-2 py-2">{r.assetTypeModel || r.assetSubTypeName}</td>
                <td className="border-r border-black px-2 py-2 text-right">{fmt(r.amount)}</td>
                <td className="px-2 py-2 text-center">{r.paymentStatus}</td>
              </tr>
            ))}

            {/* Total Row */}
            <tr className="border-t-2 border-black">
              <td colSpan="7" className="border-r border-black px-2 py-2"></td>
              <td className="border-r border-black px-2 py-2 text-right font-bold border-2 border-black">
                {fmt(totalAmount)}
              </td>
              <td className="px-2 py-2"></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-8 text-xs">
        {new Date().toLocaleDateString("en-GB")}
      </div>

      <style>{`
        @media print {
          body { margin: 0; -webkit-print-color-adjust: exact; color-adjust: exact; }
          table { border-collapse: collapse !important; }
          th, td { border: 1px solid black !important; }
          .bg-gray-100 { background-color: #f3f4f6 !important; }
          .bg-black { background-color: black !important; color: white !important; }
          @page { margin: 0.5in; size: A4 portrait; }
        }
      `}</style>
    </div>
  );
};

export default FullPaymentReportAllPrint;
